/**
 *
 * Calendar
 *
 */

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterLuxon } from '@mui/x-date-pickers-pro/AdapterLuxon';
import PropTypes from 'prop-types';
import React, { forwardRef, memo, useCallback } from 'react';

import { ArrowLeftIcon, ArrowRightIcon, PinDateIcon } from 'assets/v2/icons';
import { zoneIgnoredDate } from 'utils/helpers';

import { popperStyles } from './Calendar.styles';

class CustomLuxonAdapter extends AdapterLuxon {
  getWeekdays = () => ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  getWeekArray = (date) => {
    const startOfWeek = (d) => d.minus({ days: d.weekday % 7 });

    const start = startOfWeek(date.startOf('month'));
    const end = startOfWeek(date.endOf('month')).plus({ days: 6 });

    const totalDays = Math.round(end.diff(start, 'days').days) + 1;
    const weeks = [];

    for (let i = 0; i < totalDays; i += 1) {
      const current = start.plus({ days: i });
      const weekIndex = Math.floor(i / 7);

      if (!weeks[weekIndex]) {
        weeks[weekIndex] = [];
      }

      weeks[weekIndex].push(current);
    }

    return weeks;
  };
}

const Calendar = forwardRef(
  (
    {
      component,
      value,
      onChange,
      valid,
      error,
      renderInput,
      // Replaces the glyph inside MUI's open-picker button. Use this rather than the
      // input's end adornment to flag state on the field: swapping the glyph keeps the
      // button — and so the only way to open a desktop DatePicker — intact.
      openPickerIcon,
      components,
      ...props
    },
    ref,
  ) => {
    const Component = component || DatePicker;

    // Create a stable ref merging function using useCallback
    const createMergedRef = useCallback(
      (paramsInputRef) => (element) => {
        // Update MUI's internal ref
        if (paramsInputRef) {
          if (typeof paramsInputRef === 'function') {
            paramsInputRef(element);
          } else if (paramsInputRef.current !== undefined) {
            // eslint-disable-next-line no-param-reassign
            paramsInputRef.current = element;
          }
        }
        // Update external ref
        if (ref) {
          if (typeof ref === 'function') {
            ref(element);
          } else if (ref.current !== undefined) {
            // eslint-disable-next-line no-param-reassign
            ref.current = element;
          }
        }
      },
      [ref], // Only recreate if external ref changes
    );

    const handleRenderInput = useCallback(
      (params) => {
        const paramsWithRef = {
          ...params,
          inputRef: createMergedRef(params.inputRef),
        };
        if (renderInput) {
          return renderInput(paramsWithRef);
        }
        return null;
      },
      [createMergedRef, renderInput],
    );

    return (
      <LocalizationProvider dateAdapter={CustomLuxonAdapter}>
        <Component
          components={{
            LeftArrowIcon: ArrowLeftIcon,
            RightArrowIcon: ArrowRightIcon,
            OpenPickerIcon: openPickerIcon ?? PinDateIcon,
            // Merged rather than overwritten — a caller passing `components` used to
            // clobber the arrow icons above along with the picker icon
            ...components,
          }}
          PopperProps={{ sx: popperStyles }}
          {...props}
          renderInput={handleRenderInput}
          value={zoneIgnoredDate(value, true)}
          onChange={(changeValue) => onChange(zoneIgnoredDate(changeValue))}
          valid={valid}
          error={error}
        />
      </LocalizationProvider>
    );
  },
);

Calendar.propTypes = {
  component: PropTypes.node,
  value: PropTypes.any,
  renderInput: PropTypes.func,
  openPickerIcon: PropTypes.elementType,
  components: PropTypes.object,
  onChange: PropTypes.func,
  valid: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default memo(Calendar);
export { default as CustomizedCalendar } from './variants/Customized';
